import React, { useState, useEffect } from "react";
import { GeneratedDataItem } from "./types";
import { FaPlus, FaMinus } from "react-icons/fa";

interface TableComponentProps {
  item?: GeneratedDataItem | null | undefined;
  editMode?: boolean;
  editData?: Record<string, any>;
  handleInputChange?: (
    id: string,
    field: string,
    value: any,
    componentType?: string
  ) => void;
  heading?: string;
  slideType?: string;
  documentID?: string;
  orgId?: string;
  authToken?: string;
  setDisplayMode?: () => void;
  outlineID?: string;
  setIsSlideLoading?: () => void;
  setFailed?: () => void;
  rows?: string[][];
  columnHeaders?: string[];
  rowHeaders?: string[];
  // optional edit control callbacks from parent
  toggleEditMode?: (id: string) => void;
  saveChanges?: (id: string) => void;
  cancelEdit?: (id: string) => void;
  saving?: Record<string, boolean>;
}

interface EditableTableData {
  columnHeaders: string[];
  rowHeaders: string[];
  rows: string[][];
}

const TableComponent: React.FC<TableComponentProps> = ({
  item,
  editMode = false,
  editData,
  handleInputChange,
  heading,
  slideType,
  documentID,
  orgId,
  authToken,
  setDisplayMode,
  outlineID,
  setIsSlideLoading,
  setFailed,
  rows,
  columnHeaders,
  rowHeaders,
}) => {
  // ---------- Direct Data States ----------
  const [localDirectRows, setLocalDirectRows] = useState<string[][]>(
    rows || []
  );
  const [localDirectRowHeaders, setLocalDirectRowHeaders] = useState<string[]>(
    rowHeaders || []
  );
  const [localDirectColumnHeaders, setLocalDirectColumnHeaders] = useState<
    string[]
  >(columnHeaders || []);
  const [localTitle, setLocalTitle] = useState<string>(
    () => item?.table?.title || ""
  );

  useEffect(() => {
    // Sync incoming direct props into local state only when NOT in edit mode.
    // This avoids clobbering user input while they're typing (the parent may
    // re-create `rows`/`columnHeaders` references on every render).
    if (!editMode && rows && columnHeaders) {
      setLocalDirectRows(rows);
      setLocalDirectRowHeaders(rowHeaders || []);
      setLocalDirectColumnHeaders(columnHeaders);
    }
  }, [rows, columnHeaders, rowHeaders, editMode]);

  // Initialize from editData when entering edit mode
  useEffect(() => {
    if (editMode && editData && item?._id) {
      const currentEditData = editData;

      // Use editData arrays if available, otherwise fall back to transformed data from item.table
      if (currentEditData.columnHeaders) {
        setLocalDirectColumnHeaders(currentEditData.columnHeaders);
      }
      if (currentEditData.rowHeaders) {
        setLocalDirectRowHeaders(currentEditData.rowHeaders);
      }
      if (currentEditData.rows) {
        setLocalDirectRows(currentEditData.rows);
      }
      if (currentEditData.table?.title) {
        setLocalTitle(currentEditData.table.title);
      }
    }
  }, [editMode, editData, item?._id]);

  // If direct props are not provided, but `item.table` exists, seed direct-local state from it
  useEffect(() => {
    // Seed local state from item.table (backend shape) only when not editing.
    // If the component is in editMode we should not overwrite local edits.
    if (!editMode && (!rows || !columnHeaders) && item?.table) {
      const sourcedColumns: string[] = [];
      const sourcedRowHeaders: string[] = [];
      const sourcedRows: string[][] = [];

      // pull up to 5 column headers
      for (let i = 0; i <= 5; i++) {
        const key = `columnHeader${i}` as keyof typeof item.table;
        if ((item.table as any)[key])
          sourcedColumns.push((item.table as any)[key]);
      }

      // pull up to 8 row headers and their row objects
      for (let i = 1; i <= 8; i++) {
        const rKey = `rowHeader${i}` as keyof typeof item.table;
        const rowsKey = `rows${i}` as keyof typeof item.table;
        if ((item.table as any)[rKey]) {
          sourcedRowHeaders.push((item.table as any)[rKey]);
        }
        if ((item.table as any)[rowsKey]) {
          const rowObj = (item.table as any)[rowsKey];
          const rowArr: string[] = [];
          for (let j = 1; j <= 5; j++) {
            rowArr.push(rowObj[`attribute${j}`] || "");
          }
          sourcedRows.push(rowArr);
        }
      }

      // ensure minimum sizes
      while (sourcedColumns.length < 2) sourcedColumns.push("");
      while (sourcedRowHeaders.length < 2) sourcedRowHeaders.push("");
      while (sourcedRows.length < 2)
        sourcedRows.push(Array(Math.max(sourcedColumns.length, 2)).fill(""));

      setLocalDirectColumnHeaders((prev) =>
        sourcedColumns.length ? sourcedColumns : prev.length ? prev : ["", ""]
      );
      setLocalDirectRowHeaders((prev) =>
        sourcedRowHeaders.length
          ? sourcedRowHeaders
          : prev.length
          ? prev
          : ["", ""]
      );
      setLocalDirectRows((prev) =>
        sourcedRows.length
          ? sourcedRows
          : prev.length
          ? prev
          : [
              ["", ""],
              ["", ""],
            ]
      );

      // title
      setLocalTitle(item.table.title || "");
    }
  }, [item, rows, columnHeaders, editMode]);

  // ---------- Handlers for Direct Data Editing ----------
  const handleDirectColumnHeaderChange = (index: number, value: string) => {
    const newHeaders = [...localDirectColumnHeaders];
    newHeaders[index] = value;
    setLocalDirectColumnHeaders(newHeaders);

    if (handleInputChange) {
      // if parent passed an item id use it; otherwise still notify parent with blank id

      handleInputChange(item?._id || "", "columnHeaders", newHeaders, "table");
    }
  };

  const handleDirectRowHeaderChange = (index: number, value: string) => {
    const newHeaders = [...localDirectRowHeaders];
    newHeaders[index] = value;
    setLocalDirectRowHeaders(newHeaders);

    if (handleInputChange) {
      handleInputChange(item?._id || "", "rowHeaders", newHeaders, "table");
    }
  };

  const handleDirectCellChange = (
    rowIndex: number,
    colIndex: number,
    value: string
  ) => {
    const newRows = [...localDirectRows];
    newRows[rowIndex][colIndex] = value;
    setLocalDirectRows(newRows);

    if (handleInputChange) {
      handleInputChange(item?._id || "", "rows", newRows, "table");
    }
  };

  // ---------- Add/Remove Row/Column for Direct Mode ----------
  const handleAddDirectColumn = () => {
    if (localDirectColumnHeaders.length < 5) {
      const newHeaders = [
        ...localDirectColumnHeaders,
        `Column ${localDirectColumnHeaders.length + 1}`,
      ];
      const newRows = localDirectRows.map((row) => [...row, ""]);
      setLocalDirectColumnHeaders(newHeaders);
      setLocalDirectRows(newRows);

      if (handleInputChange) {
        handleInputChange(
          item?._id || "",
          "columnHeaders",
          newHeaders,
          "table"
        );
        handleInputChange(item?._id || "", "rows", newRows, "table");
      }
    }
  };

  const handleRemoveDirectColumn = () => {
    if (localDirectColumnHeaders.length > 2) {
      const newHeaders = localDirectColumnHeaders.slice(0, -1);
      const newRows = localDirectRows.map((row) => row.slice(0, -1));
      setLocalDirectColumnHeaders(newHeaders);
      setLocalDirectRows(newRows);

      if (handleInputChange) {
        handleInputChange(
          item?._id || "",
          "columnHeaders",
          newHeaders,
          "table"
        );
        handleInputChange(item?._id || "", "rows", newRows, "table");
      }
    }
  };

  const handleAddDirectRow = () => {
    if (localDirectRows.length < 8) {
      const newRows = [
        ...localDirectRows,
        Array(localDirectColumnHeaders.length).fill(""),
      ];
      const newHeaders = [
        ...localDirectRowHeaders,
        `Row ${localDirectRowHeaders.length + 1}`,
      ];
      setLocalDirectRows(newRows);
      setLocalDirectRowHeaders(newHeaders);

      if (handleInputChange) {
        handleInputChange(item?._id || "", "rows", newRows, "table");
        handleInputChange(item?._id || "", "rowHeaders", newHeaders, "table");
      }
    }
  };

  const handleRemoveDirectRow = () => {
    if (localDirectRows.length > 2) {
      const newRows = localDirectRows.slice(0, -1);
      const newHeaders = localDirectRowHeaders.slice(0, -1);
      setLocalDirectRows(newRows);
      setLocalDirectRowHeaders(newHeaders);

      if (item && handleInputChange) {
        handleInputChange(item._id || "", "rows", newRows, "table");
        handleInputChange(item._id || "", "rowHeaders", newHeaders, "table");
      }
    }
  };

  // ---------- Render Direct Data Mode ----------
  if (rows && columnHeaders) {
    return (
      <div 
        className="overflow-x-auto"
        style={{
          touchAction: "pan-x pan-y",
          WebkitOverflowScrolling: "touch"
        }}
      >
        <div className="input-group mb-2">
          {editMode ? (
            <div>

               <label className="block text-sm font-medium text-gray-700 mb-1">Title:</label>
           
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded"
              placeholder="Add Slide Title"
              aria-label="Add Slide Title"
              value={localTitle}
              onChange={(e) => {
                setLocalTitle(e.target.value);
                if (handleInputChange && item?._id) {
                  // keep parent in sync - store in table object
                  handleInputChange(
                    item._id,
                    "table.title",
                    e.target.value,
                    "table"
                  );
                }
              }}
            />
             </div>
          ) : (
            // <input
            //   type="text"
            //   className="form-control"
            //   placeholder="Add Slide Title"
            //   aria-label="Add Slide Title"
            //   value={localTitle}
            //   readOnly
            // />
             <h4 className="text-lg font-bold mb-2">{localTitle}</h4>
          )}
        </div>
        <table 
          className="min-w-full bg-white border border-gray-300"
          style={{
            touchAction: "pan-x pan-y"
          }}
        >
          <thead>
            <tr className="bg-gray-100">
              {/* <th className="border p-2"></th> */}
              {localDirectColumnHeaders
                .map((header, index) => ({ header, index }))
                .filter(({ header }) => editMode || header.trim() !== '')
                .map(({ header, index }) => (
                <th key={index} className="border p-2 font-semibold">
                  {editMode ? (
                    <input
                      type="text"
                      value={header}
                      onChange={(e) =>
                        handleDirectColumnHeaderChange(index, e.target.value)
                      }
                      placeholder={`Column ${index + 1}`}
                      className="w-full font-semibold text-center border-none bg-transparent focus:outline-none"
                      maxLength={25}
                    />
                  ) : (
                    <div className="text-center">{header}</div>
                  )}
                </th>
              ))}
              {editMode && (
                <th className="bg-gray-50 p-2">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={handleAddDirectColumn}
                      disabled={localDirectColumnHeaders.length >= 5}
                      className="text-blue-600 border border-gray-300 rounded-full p-1 hover:bg-blue-100"
                    >
                      <FaPlus />
                    </button>
                    <button
                      onClick={handleRemoveDirectColumn}
                      disabled={localDirectColumnHeaders.length <= 2}
                      className="text-red-600 border border-gray-300 rounded-full p-1 hover:bg-red-100"
                    >
                      <FaMinus />
                    </button>
                  </div>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {localDirectRowHeaders
              .filter((_, rowIndex) => editMode || localDirectColumnHeaders.some(h => h.trim() !== ''))
              .map((rowHeader, rowIndex) => (
              <tr key={rowIndex}>
                {(editMode || localDirectRowHeaders.some(h => h.trim() !== '')) && (
                  <th className="border p-2 font-semibold bg-gray-50">
                    {editMode ? (
                      <input
                        type="text"
                        value={rowHeader}
                        onChange={(e) =>
                          handleDirectRowHeaderChange(rowIndex, e.target.value)
                        }
                        placeholder={`Row ${rowIndex + 1}`}
                        className="w-full text-center border-none bg-transparent focus:outline-none"
                        maxLength={25}
                      />
                    ) : (
                      <div className="text-center">{rowHeader}</div>
                    )}
                  </th>
                )}
                {localDirectColumnHeaders.slice(1)
                  .map((header, colIndex) => ({ header, colIndex }))
                  .filter(({ header }) => editMode || header.trim() !== '')
                  .map(({ colIndex }) => (
                  <td key={colIndex} className="border p-2">
                    {editMode ? (
                      <input
                        type="text"
                        value={localDirectRows[rowIndex]?.[colIndex] || ""}
                        onChange={(e) =>
                          handleDirectCellChange(
                            rowIndex,
                            colIndex,
                            e.target.value
                          )
                        }
                        placeholder={`Cell ${rowIndex + 1}-${colIndex + 1}`}
                        className="w-full text-start border-none bg-transparent focus:outline-none"
                        maxLength={25}
                      />
                    ) : (
                      <div>{localDirectRows[rowIndex]?.[colIndex]}</div>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {editMode && (
            <tfoot>
              <tr>
                <td
                  className="bg-gray-50 p-2"
                  colSpan={localDirectColumnHeaders.filter(h => h.trim() !== '').length + (localDirectRowHeaders.some(h => h.trim() !== '') ? 1 : 0)}
                >
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={handleAddDirectRow}
                      disabled={localDirectRows.length >= 8}
                      className="text-blue-600 border border-gray-300 rounded-full p-1 hover:bg-blue-100"
                    >
                      <FaPlus />
                    </button>
                    <button
                      onClick={handleRemoveDirectRow}
                      disabled={localDirectRows.length <= 2}
                      className="text-red-600 border border-gray-300 rounded-full p-1 hover:bg-red-100"
                    >
                      <FaMinus />
                    </button>
                  </div>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
        {heading && (
          <p className="text-sm text-gray-500 mt-2">Source: {heading}</p>
        )}
      </div>
    );
  }

  return null; // fallback
};

export default TableComponent;
